import {
  Plug, Unplug, FileCode2, RefreshCw, DatabaseZap, Pencil,
  FileEdit, Copy, Trash2, Loader2, UserPlus,
} from 'lucide-react';
import type { ContextMenuItem } from '@/components/shared/ContextMenu';
import type { ConnectionConfig, ConnectionStatus } from '@/types';

/**
 * Callback deps for buildConnectionActions. Each caller wires its own
 * mutations / navigation / dialogs — the builder just decides WHICH items
 * to surface and with what metadata (labels, disabled state, ordering,
 * grouping, danger flag). Keeps the context menu and the Info tab's
 * ActionPanel rendering the same ordered list.
 */
export interface ConnectionActionDeps {
  onConnect: () => void;
  onDisconnect: () => void;
  onNewQuery: () => void;
  onReload: () => void;
  onCreateDatabase: () => void;
  onEdit: () => void;
  onRename: () => void;
  onCopyHostPort: () => void;
  onShare: () => void;
  onDelete: () => void;
}

export function buildConnectionActions(
  conn: ConnectionConfig,
  status: ConnectionStatus,
  deps: ConnectionActionDeps,
): ContextMenuItem[] {
  // Shared connections (someone else owns them) hide the owner-only actions.
  // The owner sees Edit/Rename/Share/Delete; recipients see only the
  // operational items so they can connect, browse, query.
  const ownedActions: ContextMenuItem[] = conn.shared
    ? []
    : [
        { label: '', separator: true, sectionTitle: 'Manage' },
        { label: 'Edit Connection…', icon: Pencil, onClick: deps.onEdit },
        { label: 'Rename…', icon: FileEdit, onClick: deps.onRename },
        { label: 'Copy Host:Port', icon: Copy, onClick: deps.onCopyHostPort },
        { label: 'Share…', icon: UserPlus, onClick: deps.onShare },
      ];

  const dangerActions: ContextMenuItem[] = conn.shared
    ? []
    : [
        { label: '', separator: true, sectionTitle: 'Danger Zone' },
        { label: 'Delete', icon: Trash2, danger: true, onClick: deps.onDelete },
      ];

  const sharedNote: ContextMenuItem[] = conn.shared
    ? [{ label: '', separator: true, sectionTitle: 'Shared with you' }]
    : [];

  const isConnected = status === 'connected';
  const isConnecting = status === 'connecting';

  // File-backed (DuckDB) connections don't have a meaningful host/port, and
  // their `databaseName` is the full path to a .duckdb file — ugly in a
  // one-line sublabel. Summarise differently for those, keep the standard
  // "host:port · dbname" layout for everything else.
  const sublabel = (() => {
    if (isConnecting) return 'Please wait…';
    if (conn.driverType === 'files') {
      return isConnected ? 'Files · connected' : 'Files · click to connect';
    }
    const base = `${conn.host}:${conn.port}`;
    return isConnected && conn.databaseName
      ? `${base} · ${conn.databaseName}`
      : base;
  })();

  return [
    // Featured primary action — the stateful toggle. Label, icon, and
    // sublabel all react to live connection status.
    {
      label: isConnecting ? 'Connecting…' : isConnected ? 'Disconnect' : 'Connect',
      icon: isConnecting ? Loader2 : isConnected ? Unplug : Plug,
      onClick: isConnected ? deps.onDisconnect : deps.onConnect,
      disabled: isConnecting,
      primary: true,
      sublabel,
    },
    // ─── Operations — working with the connection ──────────────
    { label: '', separator: true, sectionTitle: 'Operations' },
    {
      label: 'New Query',
      icon: FileCode2,
      onClick: deps.onNewQuery,
    },
    {
      label: 'Reload',
      icon: RefreshCw,
      onClick: deps.onReload,
      disabled: !isConnected,
    },
    {
      label: 'Create Database…',
      icon: DatabaseZap,
      onClick: deps.onCreateDatabase,
      disabled: !isConnected,
    },
    ...ownedActions,
    ...dangerActions,
    ...sharedNote,
  ];
}
